import * as React from 'react';
import {useMergeRefs} from '@floating-ui/react';

import {mergeProps} from '../../utils';
import {
  useSearchableListContext,
  useSearchableListSelectionContext,
} from './useSearchableList';
import {renderDefaultOption} from './SearchableListBox/_renderDefaultOption';
import type {SearchableListBoxProps} from './SearchableListBox/_types';

type UseSearchableListBoxProps = {
  ref: React.ForwardedRef<HTMLDivElement>;
} & SearchableListBoxProps;

export const useSearchableListBox = ({
  ref,
  ...props
}: UseSearchableListBoxProps) => {
  const {
    getFloatingProps,
    options,
    refs,
    renderOption: renderOptionProp,
  } = useSearchableListContext();
  const {inputRef, selectionMode} = useSearchableListSelectionContext();

  const mergedRefs = useMergeRefs([ref, refs.setFloating]);
  const floatingProps = getFloatingProps({
    ref: mergedRefs,
    onKeyDown(e) {
      /**
       * Users are focused on something within the SearchableListBox (e.g. a RowAction).
       * If they press Up or Down, we want to move focus back to the SearchableListInput, so we can navigate the list.
       *
       * Without this, they would see the active Item change, but their actual focus would be on the Action.
       * When they press space or enter, they would trigger the action for the focused RowAction and not the SearchableListRowItem in question.
       */
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        inputRef?.current?.focus();
      }
    },
  });
  const mergedProps = mergeProps(floatingProps, {
    'aria-multiselectable': selectionMode === 'multiple',
    ...props,
  });

  const renderOption = React.useMemo(
    () => renderOptionProp ?? renderDefaultOption,
    [renderOptionProp]
  );

  return React.useMemo(
    () => ({
      mergedProps,
      options,
      renderOption,
      refs,
    }),
    [mergedProps, options, renderOption, refs]
  );
};
